import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Globals } from '../globals';
import {
  LEGACY_MOTOR_FILTER_STATE_KEY,
  LEGACY_MOTOR_UI_STATE_KEY,
  LISTING_FILTER_STATE_KEY,
  LISTING_UI_STATE_KEY,
} from './listing-filter-storage.constants';

export interface ListingBrowseUIState {
  displayedItems: number;
  scrollToItemId: string | null;
}

/** @deprecated Use ListingBrowseUIState */
export type UIState = ListingBrowseUIState;

const INITIAL_UI_STATE: ListingBrowseUIState = {
  displayedItems: 20,
  scrollToItemId: null,
};

@Injectable({
  providedIn: 'root'
})
export class ListingFilterStateService {
  private uiStateSubject = new BehaviorSubject<ListingBrowseUIState>(INITIAL_UI_STATE);
  public uiState$: Observable<ListingBrowseUIState> = this.uiStateSubject.asObservable();

  constructor(private globals: Globals) {}

  get currentUIState(): ListingBrowseUIState {
    return this.uiStateSubject.value;
  }

  updateFilter(updates: Partial<typeof Globals.prototype.listingFilter>): void {
    Object.keys(updates).forEach(key => {
      (this.globals.listingFilter as any)[key] = (updates as any)[key];
    });
    this.saveFilterToStorage();
  }

  updateUIState(updates: Partial<ListingBrowseUIState>): void {
    const newState = { ...this.currentUIState, ...updates };
    this.uiStateSubject.next(newState);
    this.saveUIToStorage(newState);
  }

  saveScrollState(itemId: string, displayedItems: number): void {
    this.updateUIState({ scrollToItemId: itemId, displayedItems });
    sessionStorage.setItem('scrollToItemId', itemId);
    sessionStorage.setItem('displayedItems', displayedItems.toString());
  }

  getScrollState(): { itemId: string | null; displayedItems: number } {
    return {
      itemId: sessionStorage.getItem('scrollToItemId'),
      displayedItems: parseInt(sessionStorage.getItem('displayedItems') || '20', 10)
    };
  }

  clearScrollState(): void {
    this.updateUIState({ scrollToItemId: null });
    sessionStorage.removeItem('scrollToItemId');
    sessionStorage.removeItem('displayedItems');
  }

  resetFilters(): void {
    Object.keys(this.globals.listingFilter).forEach(key => {
      if (key !== 'category' && key !== 'username' && key !== 'profile') {
        (this.globals.listingFilter as any)[key] = null;
      }
    });
    this.globals.listingFilter.attributes = {};
    this.clearStorage();
  }

  private saveFilterToStorage(): void {
    const cleanFilter: Record<string, unknown> = {};
    Object.keys(this.globals.listingFilter).forEach(key => {
      if (isNaN(Number(key))) {
        cleanFilter[key] = (this.globals.listingFilter as any)[key];
      }
    });

    const filterState = {
      listingFilter: cleanFilter,
      selectedFilter: this.globals.selectedFilter,
      location: this.globals.location,
    };
    localStorage.setItem(LISTING_FILTER_STATE_KEY, JSON.stringify(filterState));
    localStorage.removeItem(LEGACY_MOTOR_FILTER_STATE_KEY);
  }

  private saveUIToStorage(uiState: ListingBrowseUIState): void {
    localStorage.setItem(LISTING_UI_STATE_KEY, JSON.stringify(uiState));
    localStorage.removeItem(LEGACY_MOTOR_UI_STATE_KEY);
  }

  loadStateIfFromItemDetail(): void {
    const fromItemDetail = sessionStorage.getItem('fromItemDetail');
    if (fromItemDetail) {
      sessionStorage.removeItem('fromItemDetail');
      this.loadFromStorage();
    }
  }

  private loadFromStorage(): void {
    if (!this.globals.listingFilter) {
      this.globals.listingFilter = {} as any;
    }

    const savedFilter = localStorage.getItem(LISTING_FILTER_STATE_KEY)
      ?? localStorage.getItem(LEGACY_MOTOR_FILTER_STATE_KEY);
    if (savedFilter) {
      try {
        const state = JSON.parse(savedFilter);
        const persisted = state.listingFilter ?? state.vehFilter ?? {};
        Object.keys(persisted).forEach(key => {
          if (isNaN(Number(key))) {
            (this.globals.listingFilter as any)[key] = persisted[key];
          }
        });

        this.globals.selectedFilter = state.selectedFilter ?? true;
        this.globals.location = state.location ?? 'All';
      } catch (e) {
        console.error('Failed to load filter state', e);
      }
    }

    const savedUI = localStorage.getItem(LISTING_UI_STATE_KEY)
      ?? localStorage.getItem(LEGACY_MOTOR_UI_STATE_KEY);
    if (savedUI) {
      try {
        const uiState = JSON.parse(savedUI);
        this.uiStateSubject.next({
          displayedItems: uiState.displayedItems ?? INITIAL_UI_STATE.displayedItems,
          scrollToItemId: uiState.scrollToItemId ?? null,
        });
      } catch (e) {
        console.error('Failed to load UI state', e);
      }
    }

    const scrollId = sessionStorage.getItem('scrollToItemId');
    const displayedItems = sessionStorage.getItem('displayedItems');
    if (scrollId) {
      this.updateUIState({
        scrollToItemId: scrollId,
        displayedItems: parseInt(displayedItems || '20', 10)
      });
    }
  }

  private clearStorage(): void {
    localStorage.removeItem(LISTING_FILTER_STATE_KEY);
    localStorage.removeItem(LEGACY_MOTOR_FILTER_STATE_KEY);
    localStorage.removeItem(LISTING_UI_STATE_KEY);
    localStorage.removeItem(LEGACY_MOTOR_UI_STATE_KEY);
    this.clearScrollState();
  }
}

/** @deprecated Use ListingFilterStateService */
export { ListingFilterStateService as MotorFilterStateService };
